import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useNotify } from '@/api/notify';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { controlClass, Field } from '@/components/Field';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';

import { platformKeys, setPlanPrices, usePlans } from './api';
import {
  draftFrom,
  periodWords,
  priceProblems,
  priceSlots,
  pricesRequest,
  type PriceDraft,
} from './prices';

/**
 * One package's list prices, edited as a page — the insert/update rule. Four slots, each blank
 * when the package is not offered that way; saving sends the whole list, so a slot cleared here is
 * withdrawn. The licence slot takes two figures, the fee and its yearly AMC, and needs both.
 */
export function PlanPricesPage({ planCode, onBack }: { planCode: string; onBack: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const plans = usePlans();
  const fields = useAutoFocusFirstField<HTMLDivElement>();
  const plan = plans.data?.find((p) => p.code === planCode);

  const [draft, setDraft] = useState<PriceDraft | null>(null);
  const [busy, setBusy] = useState(false);

  // Filled once the plan arrives, and never again: a refetch must not overwrite what is being typed.
  useEffect(() => {
    if (plan && !draft) setDraft(draftFrom(plan.prices));
  }, [plan, draft]);

  const problems = draft ? priceProblems(draft) : [];

  const set = (key: string, field: 'recurring' | 'oneTime') => (value: string) =>
    setDraft((d) => (d ? { ...d, [key]: { ...d[key]!, [field]: value } } : d));

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await setPlanPrices(planCode, pricesRequest(draft));
      await queryClient.invalidateQueries({ queryKey: platformKeys.plans });
      notify.success(`${plan?.name ?? planCode} prices saved.`);
      onBack();
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  const amount = (key: string, field: 'recurring' | 'oneTime', label: string, hint: string) => (
    <Field label={label} hint={hint}>
      <div className="relative">
        <span className="text-default pointer-events-none absolute inset-y-0 start-3 flex items-center">
          ₹
        </span>
        <input
          className={`${controlClass} ps-7 text-end`}
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={draft?.[key]?.[field] ?? ''}
          onChange={(event) => set(key, field)(event.target.value)}
          onWheel={(event) => event.currentTarget.blur()}
        />
      </div>
    </Field>
  );

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to packages"
          className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
        >
          <i className="icon icon-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">{plan ? `${plan.name} prices` : 'Prices'}</h1>
          <p className="page-subtitle">
            Leave a way of charging blank if this package is not sold that way. A company already on
            terms keeps the price it agreed.
          </p>
        </div>
      </div>

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
          <Card>
            <SectionTitle icon="icon icon-calendar-sync" hue="indigo">
              Subscription
            </SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {priceSlots
                .filter((s) => s.chargingModel === 'Subscription')
                .map((slot) => (
                  <div key={slot.key}>
                    {amount(
                      slot.key,
                      'recurring',
                      slot.label,
                      `Charged ${periodWords[slot.billingPeriod]}.`,
                    )}
                  </div>
                ))}
            </div>
          </Card>

          <Card>
            <SectionTitle icon="icon icon-key-round" hue="purple">
              Licence + AMC
            </SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {amount('licence', 'oneTime', 'Licence fee', 'Paid once. Covers the first year.')}
              {amount('licence', 'recurring', 'AMC', 'Paid every year after the first.')}
            </div>
          </Card>
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          {problems.length > 0 && (
            <Notice tone="warning" title="Before saving">
              <ul className="ps-0">
                {problems.map((p) => (
                  <li key={p} className="list-none">
                    {p}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          <Card>
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                disabled={busy || !draft || problems.length > 0}
                onClick={() => void save()}
              >
                {busy ? 'Saving…' : 'Save prices'}
              </Button>
              <Button disabled={busy} shortcut="back" onClick={onBack}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
